// API Error Response Types
export interface ApiErrorResponse {
  detail: string;
  code?: string;
  errors?: Record<string, string[]>;
}

export interface ValidationErrorResponse {
  detail: {
    msg: string;
    type: string;
    loc: (string | number)[];
  }[];
}

// Common error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your internet connection.',
  SERVER_ERROR: 'Server error. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  UNAUTHORIZED: 'Invalid credentials. Please try again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  TIMEOUT: 'Request timed out. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

// Extract error message from API response
export const extractErrorMessage = (error: any): string => {
  // If it's a network error
  if (!error.response && error.message) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  const status = error.status || error.response?.status;
  const data = error.data || error.response?.data;

  switch (status) {
    case 400:
      return data?.detail || 'Bad request. Please check your input.';
    
    case 401:
      return data?.detail || ERROR_MESSAGES.UNAUTHORIZED;
    
    case 403:
      return data?.detail || ERROR_MESSAGES.FORBIDDEN;
    
    case 404:
      return data?.detail || ERROR_MESSAGES.NOT_FOUND;
    
    case 422:
      // Handle validation errors from FastAPI
      if (Array.isArray(data?.detail)) {
        const validationErrors = data.detail as ValidationErrorResponse['detail'];
        return formatValidationErrors(validationErrors);
      }
      return data?.detail || ERROR_MESSAGES.VALIDATION_ERROR;
    
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    
    case 500:
    case 502:
    case 503:
    case 504:
      return ERROR_MESSAGES.SERVER_ERROR;
    
    default:
      // Try to extract message from data
      if (data?.detail) {
        return data.detail;
      }
      if (data?.message) {
        return data.message;
      }
      return ERROR_MESSAGES.UNKNOWN_ERROR;
  }
};

// Format FastAPI validation errors into readable messages
export const formatValidationErrors = (errors: ValidationErrorResponse['detail']): string => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return ERROR_MESSAGES.VALIDATION_ERROR;
  }

  // Group errors by field
  const fieldErrors: Record<string, string[]> = {};
  
  errors.forEach((error) => {
    const field = error.loc[error.loc.length - 1]?.toString() || 'unknown';
    const message = error.msg;
    
    if (!fieldErrors[field]) {
      fieldErrors[field] = [];
    }
    fieldErrors[field].push(message);
  });

  // Format into readable message
  const errorMessages = Object.entries(fieldErrors).map(([field, messages]) => {
    const fieldName = formatFieldName(field);
    return `${fieldName}: ${messages.join(', ')}`;
  });

  return errorMessages.join('; ');
};

// Convert field names to readable format
export const formatFieldName = (field: string): string => {
  const fieldMap: Record<string, string> = {
    first_name: 'First Name',
    last_name: 'Last Name',
    middle_name: 'Middle Name',
    date_of_birth: 'Date of Birth',
    email: 'Email Address',
    phone: 'Phone Number',
    nin: 'NIN',
    address_line1: 'Address Line 1',
    address_line2: 'Address Line 2',
    city: 'City',
    state: 'State',
    postal_code: 'Postal Code',
    country: 'Country',
    nationality: 'Nationality',
    employer: 'Employer',
    password: 'Password',
    confirm_password: 'Confirm Password',
    accept_terms: 'Terms and Conditions',
  };

  return fieldMap[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Extract field-specific errors for form validation
export const extractFieldErrors = (error: any): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};
  const data = error.data || error.response?.data;
  
  if (data?.detail && Array.isArray(data.detail)) {
    const validationErrors = data.detail as ValidationErrorResponse['detail'];
    
    validationErrors.forEach((error) => {
      const field = error.loc[error.loc.length - 1]?.toString();
      if (field) {
        fieldErrors[field] = error.msg;
      }
    });
  }
  
  return fieldErrors;
};

// Check if error is a specific type
export const isNetworkError = (error: any): boolean => {
  return !error.response && !!error.message;
};

export const isValidationError = (error: any): boolean => {
  const status = error.status || error.response?.status;
  return status === 422;
};

export const isAuthError = (error: any): boolean => {
  const status = error.status || error.response?.status;
  return status === 401 || status === 403;
};

export const isServerError = (error: any): boolean => {
  const status = error.status || error.response?.status;
  return status >= 500 && status < 600;
};

// Log error for debugging (in development)
export const logError = (error: any, context?: string): void => {
  if (import.meta.env.VITE_DEV_MODE === 'true') {
    console.group(`🚨 ${context || 'API Error'}`);
    console.error('Error object:', error);
    console.error('Status:', error.status || error.response?.status);
    console.error('Data:', error.data || error.response?.data);
    console.error('Message:', extractErrorMessage(error));
    console.groupEnd();
  }
};